'use client'

/**
 * Distribution tables for /data/capacity-trends — brand-standard treatment
 * matching the brand-index, price-index, and energy-index tables (white card,
 * gray-50 uppercase header, hairline dividers, right-aligned tabular numerals,
 * overflow-x scroll container), click-to-sort via the shared SortableTable.
 *
 * Every value arrives already-derived from app/data/capacity-trends/page.tsx,
 * which owns the single importer of app/data/_data/capacity-trends.json —
 * sorting only reorders, it never derives. Figures render in cubic feet with at
 * most two decimals; a stated capacity is never rounded up to a whole number.
 */
import { SortableTable, type SortableColumn } from '../_components/sortable-table'
import { EntityLink, type LinkTarget } from '../_components/entity-link'
import { formatCuft, type DistRow } from './capacity-trends-summary'

/** A segment row plus the live page the server resolved for it (FRIDGE-334). */
export type LinkedDistRow = DistRow & { link?: LinkTarget }

function fmtInt(n: number): string {
  return n.toLocaleString('en-US')
}

/**
 * The shared distribution table. `firstHeader` names the segment column
 * ("Type" or "Brand"); every other column is identical across both tables so
 * the type and brand views read as one system.
 */
function CapacityDistTable({
  rows,
  firstHeader,
  caption,
}: {
  rows: LinkedDistRow[]
  firstHeader: string
  caption: string
}) {
  const columns: SortableColumn<LinkedDistRow>[] = [
    {
      key: 'label',
      header: firstHeader,
      sortValue: (r) => r.label,
      render: (r) => (
        <EntityLink link={r.link} className="font-semibold">
          {r.label}
        </EntityLink>
      ),
    },
    {
      key: 'n',
      header: 'Models',
      numeric: true,
      sortValue: (r) => r.n,
      render: (r) => <span className="font-bold">{fmtInt(r.n)}</span>,
    },
    {
      key: 'median',
      header: 'Median ft³',
      numeric: true,
      sortValue: (r) => r.median,
      render: (r) => <span className="font-semibold">{formatCuft(r.median)}</span>,
    },
    {
      key: 'p25',
      header: 'P25 ft³',
      numeric: true,
      sortValue: (r) => r.p25,
      render: (r) => <span className="text-gray-600">{formatCuft(r.p25)}</span>,
    },
    {
      key: 'p75',
      header: 'P75 ft³',
      numeric: true,
      sortValue: (r) => r.p75,
      render: (r) => <span className="text-gray-600">{formatCuft(r.p75)}</span>,
    },
    {
      key: 'range',
      header: 'P10–P90 ft³',
      numeric: true,
      sortValue: (r) => r.p90 - r.p10,
      render: (r) => (
        <span className="text-gray-500">
          {formatCuft(r.p10)}–{formatCuft(r.p90)}
        </span>
      ),
    },
    {
      key: 'span',
      header: 'Full range ft³',
      numeric: true,
      sortValue: (r) => r.max - r.min,
      render: (r) => (
        <span className="text-gray-500">
          {formatCuft(r.min)}–{formatCuft(r.max)}
        </span>
      ),
    },
  ]
  return (
    <SortableTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.label}
      defaultSortKey="median"
      defaultDir="desc"
      minWidth={860}
      caption={caption}
    />
  )
}

export function CapacityTypeTable({ rows }: { rows: LinkedDistRow[] }) {
  return (
    <CapacityDistTable
      rows={rows}
      firstHeader="Appliance type"
      caption="Refrigerator capacity distribution by appliance type, sortable | Fridge.com"
    />
  )
}

export function CapacityBrandTable({ rows }: { rows: LinkedDistRow[] }) {
  return (
    <CapacityDistTable
      rows={rows}
      firstHeader="Brand"
      caption="Refrigerator capacity distribution by brand, sortable | Fridge.com"
    />
  )
}
